/**
 * ResolutionService (AJIP-16.02.006 · WS3)
 * ---------------------------------------------------------------------
 * The single Journey Resolution Service.
 *
 * Additional Founder Directive (AJIP-16.02.006):
 *   The Resolution Service is the sole authority for determining which
 *   journey may proceed to activation. No implementation may activate
 *   a journey without first producing a valid Resolution Decision.
 *
 * The service:
 *   * Accepts a validated Resolution Request.
 *   * Consumes the protected RegistrationService, DiscoveryService and
 *     EligibilityEvaluator.
 *   * Produces a deterministic, immutable Resolution Decision.
 *
 * The service NEVER:
 *   * Registers journeys.
 *   * Modifies journey definitions.
 *   * Activates journeys.
 *   * Persists state.
 *   * Renders output.
 *   * Reads or writes storage.
 *   * Invokes providers.
 *   * Determines outcomes via AI. (AI may recommend; deterministic
 *     business rules govern the decision.)
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { FlagDisabledError } from '../core/JourneyErrors';
import { RegistrationService } from '../registrationDiscovery/RegistrationService';
import { DiscoveryService } from '../registrationDiscovery/DiscoveryService';
import { evaluateEligibility, DECISIONS as ELIG } from '../registrationDiscovery/EligibilityEvaluator';
import { validateResolutionRequest } from './ResolutionRequestValidator';
import { resolveVersion, VERSION_OUTCOMES, versionOutcomeToDecision } from './VersionResolutionPolicy';
import { detect as detectConflict } from './ConflictDetector';
import { plan as planRestoration, ACTIONS as RESTORE_ACTIONS } from './RestorationResolver';
import {
  RESOLUTION_DECISIONS,
  CONFLICT_STATUSES,
  makeResolutionDecision,
} from './ResolutionDecisionContract';
import {
  RESOLUTION_ACTIVATION_EVENTS,
  ResolutionActivationObservability,
} from './ResolutionActivationObservability';
import {
  InvalidResolutionRequestError,
  JourneyNotFoundError,
} from './ResolutionActivationErrors';

const _requireEnabled = (svc) => {
  if (!svc._enabled) throw new FlagDisabledError('JOURNEYS_ENABLED is false. ResolutionService is inert.');
};

class ResolutionService {
  constructor({
    registrationService,
    discoveryService,
    engine,
    persistenceService = null,
    idempotencyStore = null,
    observability,
    nowFn = () => Date.now(),
  } = {}) {
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED');
    if (this._enabled) {
      if (!(registrationService instanceof RegistrationService)) {
        throw new Error('ResolutionService requires a RegistrationService');
      }
      if (!(discoveryService instanceof DiscoveryService)) {
        throw new Error('ResolutionService requires a DiscoveryService');
      }
      if (!engine) {
        throw new Error('ResolutionService requires an engine reference');
      }
    }
    this._registrationService = registrationService || null;
    this._discoveryService = discoveryService || null;
    this._engine = engine || null;
    this._persistence = persistenceService;
    this._idempotency = idempotencyStore;
    this._observability = observability instanceof ResolutionActivationObservability
      ? observability
      : new ResolutionActivationObservability();
    this._now = nowFn;
    this._audit = [];
  }

  get enabled() { return this._enabled; }
  get observability() { return this._observability; }
  auditLog() { return Object.freeze(this._audit.slice()); }

  _emit(kind, payload) { this._observability.emit(kind, payload); }
  _record(entry) { this._audit.push(Object.freeze({ at: this._now(), ...entry })); }

  _rejected(request, decisionKind, reason, extras = {}) {
    const decision = makeResolutionDecision({
      requestId: request && request.requestId,
      journeyId: (request && request.journeyId) || (extras.journeyId || null),
      journeyVersion: (extras.journeyVersion == null && request) ? (request.journeyVersion || null) : extras.journeyVersion,
      decision: decisionKind,
      reason,
      activationPermitted: false,
      restorationPermitted: false,
      appliedRules: extras.appliedRules || [],
      conflictStatus: extras.conflictStatus || CONFLICT_STATUSES.NONE,
      auditMetadata: extras.auditMetadata || {},
      selectedDefinitionRef: extras.selectedDefinitionRef || null,
      eligibilityDecisionRef: extras.eligibilityDecisionRef || null,
      nowFn: this._now,
    });
    this._emit(RESOLUTION_ACTIVATION_EVENTS.RESOLUTION_REJECTED, {
      requestId: decision.requestId, decision: decisionKind, reason,
    });
    this._record({ action: 'resolve', ok: false, requestId: decision.requestId, decision: decisionKind, reason });
    return decision;
  }

  async resolve(request) {
    _requireEnabled(this);
    this._emit(RESOLUTION_ACTIVATION_EVENTS.RESOLUTION_REQUESTED, {
      requestId: request && request.requestId, entryPoint: request && request.entryPoint,
    });

    // WS2 — validation.
    try {
      validateResolutionRequest(request);
    } catch (e) {
      return this._rejected(request, RESOLUTION_DECISIONS.INVALID_REQUEST, e.message, {
        appliedRules: ['request-validation'],
      });
    }

    // WS3 step 1 — registration lookup.
    const journeyId = request.journeyId || null;
    if (!journeyId) {
      // journeyType-only resolution is not authorised in this package
      // (would require ambiguity handling not covered by workstreams).
      return this._rejected(request, RESOLUTION_DECISIONS.INVALID_REQUEST,
        'journeyId is required for resolution in this package',
        { appliedRules: ['journey-id-required'] });
    }
    const registered = this._registrationService.getRegistrationStatus(journeyId);
    if (!registered) {
      return this._rejected(request, RESOLUTION_DECISIONS.NOT_FOUND, `JOURNEY_${journeyId}_NOT_REGISTERED`, {
        appliedRules: ['registration-lookup'], journeyId,
      });
    }

    // WS3 step 2 — full definition via Discovery.
    const definition = this._discoveryService.getById(journeyId);
    if (!definition) {
      // Extremely defensive — Registration reports registered but Discovery cannot find.
      throw new JourneyNotFoundError(`Registered journey '${journeyId}' has no discoverable definition`);
    }

    // WS5 — version resolution.
    const versionOutcome = resolveVersion({
      definition,
      requestedVersion: request.journeyVersion != null ? request.journeyVersion : null,
    });
    if (versionOutcome.outcome !== VERSION_OUTCOMES.RESOLVED) {
      const decisionKind = versionOutcomeToDecision(versionOutcome.outcome);
      this._emit(RESOLUTION_ACTIVATION_EVENTS.VERSION_REJECTED, {
        requestId: request.requestId, journeyId, reason: versionOutcome.reason,
      });
      return this._rejected(request, decisionKind, versionOutcome.reason, {
        appliedRules: ['version-resolution'], journeyId,
        journeyVersion: definition.journeyVersion,
        auditMetadata: versionOutcome.replacementJourneyId
          ? { replacementJourneyId: versionOutcome.replacementJourneyId } : {},
      });
    }
    this._emit(RESOLUTION_ACTIVATION_EVENTS.VERSION_RESOLVED, {
      requestId: request.requestId, journeyId, version: versionOutcome.chosen,
    });

    // WS3 step 5 — feature-flag prerequisites declared by the journey.
    const activeFlags = new Set((request.discoveryContext && request.discoveryContext.activeFeatureFlags) || []);
    for (const flag of definition.requiredFeatureFlags || []) {
      if (!activeFlags.has(flag)) {
        return this._rejected(request, RESOLUTION_DECISIONS.FEATURE_DISABLED, `FLAG_${flag}_DISABLED`, {
          appliedRules: ['journey-feature-flags'], journeyId,
          journeyVersion: definition.journeyVersion,
        });
      }
    }

    // WS3 step 6 — deterministic eligibility.
    const eligibility = evaluateEligibility({
      definition,
      context: request.discoveryContext,
      nowFn: this._now,
    });
    if (eligibility.decision !== ELIG.ELIGIBLE) {
      const map = {
        [ELIG.INELIGIBLE]: RESOLUTION_DECISIONS.INELIGIBLE,
        [ELIG.UNAVAILABLE]: RESOLUTION_DECISIONS.INELIGIBLE,
        [ELIG.DEPRECATED]: RESOLUTION_DECISIONS.DEPRECATED,
        [ELIG.MISSING_PERMISSION]: RESOLUTION_DECISIONS.PERMISSION_DENIED,
        [ELIG.FEATURE_DISABLED]: RESOLUTION_DECISIONS.FEATURE_DISABLED,
        [ELIG.MISSING_PREREQUISITE]: RESOLUTION_DECISIONS.MISSING_PREREQUISITE,
        [ELIG.CONFLICT_DETECTED]: RESOLUTION_DECISIONS.CONFLICT,
        [ELIG.BLOCKED]: RESOLUTION_DECISIONS.CONFLICT,
      };
      return this._rejected(request, map[eligibility.decision] || RESOLUTION_DECISIONS.INELIGIBLE, eligibility.reason, {
        appliedRules: ['eligibility'], journeyId,
        journeyVersion: definition.journeyVersion,
        eligibilityDecisionRef: eligibility.reason,
      });
    }

    // WS6 — conflict detection (Founder Ruling A1: at Resolution).
    const conflict = detectConflict({
      request,
      definition,
      engine: this._engine,
      registrationService: this._registrationService,
      idempotencyStore: this._idempotency,
    });
    if (conflict.hasConflict) {
      this._emit(RESOLUTION_ACTIVATION_EVENTS.CONFLICT_DETECTED, {
        requestId: request.requestId, journeyId, kind: conflict.kind,
      });
      return this._rejected(request, RESOLUTION_DECISIONS.CONFLICT, conflict.kind, {
        appliedRules: ['conflict-detection'], journeyId,
        journeyVersion: definition.journeyVersion,
        conflictStatus: conflict.kind,
      });
    }

    // WS9 — determine activation vs restoration action.
    const restorePlan = await planRestoration({
      engine: this._engine,
      persistenceService: this._persistence,
      request,
      definition,
    });

    let decisionKind = RESOLUTION_DECISIONS.RESOLVED;
    let activationPermitted = true;
    let restorationPermitted = false;
    if (restorePlan.action === RESTORE_ACTIONS.RESUME_INMEMORY
        || restorePlan.action === RESTORE_ACTIONS.RESTORE_PERSISTED) {
      decisionKind = RESOLUTION_DECISIONS.RESTORATION_REQUIRED;
      activationPermitted = false;
      restorationPermitted = true;
    } else if (restorePlan.action === RESTORE_ACTIONS.REJECT_ACTIVATION) {
      decisionKind = RESOLUTION_DECISIONS.ACTIVATION_PROHIBITED;
      activationPermitted = false;
      restorationPermitted = false;
    }

    const decision = makeResolutionDecision({
      requestId: request.requestId,
      journeyId,
      journeyVersion: definition.journeyVersion,
      decision: decisionKind,
      reason: 'OK',
      eligibilityDecisionRef: eligibility.reason,
      selectedDefinitionRef: journeyId,
      activationPermitted,
      restorationPermitted,
      appliedRules: [
        'request-validation', 'registration-lookup', 'version-resolution',
        'journey-feature-flags', 'eligibility', 'conflict-detection', 'restoration-plan',
      ],
      auditMetadata: {
        restorationAction: restorePlan.action,
        persistenceRecordId: restorePlan.persistenceRecordId || null,
      },
      nowFn: this._now,
    });

    this._emit(RESOLUTION_ACTIVATION_EVENTS.RESOLUTION_COMPLETED, {
      requestId: decision.requestId, journeyId, decision: decisionKind,
    });
    this._record({ action: 'resolve', ok: true, requestId: decision.requestId, decision: decisionKind });
    return decision;
  }
}

export { ResolutionService };
